import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Progress } from '../../ui/progress';
import { toast } from 'sonner';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import { 
  FileText,
  CheckCircle,
  ArrowRight,
  Trash2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MinhasAvaliacoesFitCultural = () => {
  const { user } = useAuth();
  const [minhasAvaliacoes, setMinhasAvaliacoes] = useState([]);
  const [avaliacaoAtiva, setAvaliacaoAtiva] = useState(null);
  const [perguntaAtual, setPerguntaAtual] = useState(0);
  const [respostas, setRespostas] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    carregarMinhasAvaliacoes();
  }, []);

  const carregarMinhasAvaliacoes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/fit-cultural/minhas-avaliacoes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMinhasAvaliacoes(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar minhas avaliações:', error);
      toast.error('Erro ao carregar avaliações');
    }
  };

  const handleExcluir = async (id, nome) => {
    if (!window.confirm(`Tem certeza que deseja excluir a avaliação "${nome}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/fit-cultural/avaliacoes-aplicadas/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Avaliação excluída com sucesso');
      await carregarMinhasAvaliacoes();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir avaliação');
    }
  };

  const iniciarAvaliacao = (avaliacao) => {
    setAvaliacaoAtiva(avaliacao);
    setPerguntaAtual(0);
    setRespostas({});
  };

  const handleResposta = (perguntaId, valor) => {
    setRespostas(prev => ({
      ...prev,
      [perguntaId]: valor
    }));
  };

  const proximaPergunta = () => {
    const perguntas = avaliacaoAtiva.perguntas || [];
    if (perguntaAtual < perguntas.length - 1) {
      setPerguntaAtual(perguntaAtual + 1);
    }
  };

  const perguntaAnterior = () => {
    if (perguntaAtual > 0) {
      setPerguntaAtual(perguntaAtual - 1);
    }
  };

  const finalizarAvaliacao = async () => {
    const perguntas = avaliacaoAtiva.perguntas || [];
    const respostasCompletas = perguntas.every(p => respostas[p.id] !== undefined);

    if (!respostasCompletas) {
      toast.error('Por favor, responda todas as perguntas');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/fit-cultural/responder/${avaliacaoAtiva.id}`,
        { respostas },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Avaliação concluída com sucesso!');
      setAvaliacaoAtiva(null);
      setPerguntaAtual(0);
      setRespostas({});
      await carregarMinhasAvaliacoes();
    } catch (error) {
      console.error('Erro ao finalizar avaliação:', error);
      toast.error(error.response?.data?.detail || 'Erro ao finalizar avaliação');
    } finally {
      setLoading(false);
    }
  };

  const perguntas = avaliacaoAtiva?.perguntas || [];
  const pergunta = perguntas[perguntaAtual];
  const progresso = perguntas.length > 0 ? ((perguntaAtual + 1) / perguntas.length) * 100 : 0;

  if (avaliacaoAtiva) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">{avaliacaoAtiva.avaliacao_nome}</h1>
            <p className="text-gray-600">Pergunta {perguntaAtual + 1} de {perguntas.length}</p>
          </div>
          <Button variant="outline" onClick={() => setAvaliacaoAtiva(null)}>
            Voltar
          </Button>
        </div>

        <Progress value={progresso} className="h-2" />

        {pergunta && (
          <Card>
            <CardHeader>
              <CardTitle>Pergunta {perguntaAtual + 1}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-lg">{pergunta.texto}</p>

              <div className="space-y-3">
                <div className="flex gap-3 justify-center">
                  {[1, 2, 3, 4, 5].map(valor => (
                    <button
                      key={valor}
                      onClick={() => handleResposta(pergunta.id, valor)}
                      className={`w-16 h-16 rounded-full border-2 transition-all ${
                        respostas[pergunta.id] === valor
                          ? 'bg-blue-500 text-white border-blue-500 scale-110'
                          : 'bg-white hover:bg-gray-100 border-gray-300'
                      }`}
                    >
                      {valor}
                    </button>
                  ))}
                </div>

                <div className="flex justify-between text-sm text-gray-600">
                  <span>Discordo Totalmente</span>
                  <span>Concordo Totalmente</span>
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <Button
                  variant="outline"
                  onClick={perguntaAnterior}
                  disabled={perguntaAtual === 0}
                >
                  Anterior
                </Button>

                {perguntaAtual === perguntas.length - 1 ? (
                  <Button
                    onClick={finalizarAvaliacao}
                    disabled={!respostas[pergunta.id] || loading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {loading ? 'Finalizando...' : 'Finalizar Avaliação'}
                  </Button>
                ) : (
                  <Button
                    onClick={proximaPergunta}
                    disabled={!respostas[pergunta.id]}
                  >
                    Próxima
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Minhas Avaliações</h1>
        <p className="text-gray-600 mt-1">Avaliações de Fit Cultural atribuídas a você</p>
      </div>

      {minhasAvaliacoes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">Você não possui avaliações pendentes no momento</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {minhasAvaliacoes.map(aval => (
            <Card key={aval.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2">{aval.avaliacao_nome}</h3>
                    <p className="text-gray-600 mb-4">{aval.avaliacao_descricao}</p>
                    <p className="text-sm text-gray-500">
                      {aval.perguntas?.length || 0} perguntas
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {user?.is_admin && (
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => handleExcluir(aval.id, aval.avaliacao_nome)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                    <Button onClick={() => iniciarAvaliacao(aval)}>
                      Iniciar
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MinhasAvaliacoesFitCultural;
